import { Plus, FileText } from "lucide-react";
import { Button } from "../ui/button";

function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function planPreview(plan) {
  const data = plan.type === "evaluation" ? plan.evaluation : plan.daily;
  return (
    data?.assessment ||
    data?.objective ||
    data?.history ||
    plan?.patient?.summary ||
    plan?.patient?.transcript ||
    "(empty)"
  );
}

export default function PatientDashboard({ patient, onNewPlan, onOpenPlan }) {
  const sortedPlans = [...patient.plans].sort((a, b) =>
    a.date < b.date ? 1 : -1
  );

  return (
    <div data-testid="patient-dashboard" className="space-y-6">
      {/* Patient header card */}
      <div className="bg-white rounded-3xl border border-slate-200/80 shadow-soft p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-5">
        <div className="flex items-center gap-4 flex-1 min-w-0">
          <span className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#D1FAE5] text-[#065F46] font-outfit text-xl font-bold flex-shrink-0">
            {patient.initials}
          </span>
          <div className="min-w-0">
            <div className="font-figtree text-xs uppercase tracking-[0.18em] font-semibold text-[#94A3B8]">
              Patient ID
            </div>
            <div className="font-outfit text-2xl md:text-3xl font-bold tracking-tight text-[#0F172A]">
              {patient.id}
            </div>
            <div className="mt-1 flex items-center gap-2 font-figtree text-sm text-[#64748B]">
              <FileText className="w-4 h-4 text-[#059669]" />
              {patient.plans.length}{" "}
              {patient.plans.length === 1 ? "plan on file" : "plans on file"}
            </div>
          </div>
        </div>

        <Button
          data-testid="patient-dashboard-new-plan-btn"
          onClick={onNewPlan}
          className="rounded-full bg-[#10B981] hover:bg-[#059669] text-white font-figtree text-sm font-medium px-6 py-6 shadow-[0_8px_20px_rgba(16,185,129,0.35)] transition-all hover:-translate-y-0.5 self-stretch md:self-auto"
        >
          <Plus className="w-4 h-4 mr-2" />
          New plan
        </Button>
      </div>

      {/* Timeline */}
      <div className="bg-white rounded-3xl border border-slate-200/80 shadow-soft p-6 md:p-8">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="font-outfit text-xl font-semibold tracking-tight text-[#0F172A]">
              Patient history
            </h2>
            <p className="font-figtree text-sm text-[#64748B] mt-0.5">
              Every visit for this patient, newest first.
            </p>
          </div>
          <span className="font-figtree text-xs text-[#94A3B8]">
            {sortedPlans.length}{" "}
            {sortedPlans.length === 1 ? "entry" : "entries"}
          </span>
        </div>

        {sortedPlans.length === 0 ? (
          <div className="text-center py-12 font-figtree text-sm text-[#94A3B8]">
            No plans yet — click "New plan" to document the first visit.
          </div>
        ) : (
          <ol className="relative border-l-2 border-[#D1FAE5] ml-3 space-y-5">
            {sortedPlans.map((plan) => (
              <li
                key={plan.id}
                data-testid={`timeline-plan-${plan.id}`}
                className="pl-6 relative"
              >
                <span
                  className={`absolute -left-[9px] top-2 w-4 h-4 rounded-full border-2 border-white ${
                    plan.type === "evaluation" ? "bg-[#0F172A]" : "bg-[#10B981]"
                  }`}
                />
                <button
                  onClick={() => onOpenPlan(plan)}
                  className="block w-full text-left rounded-2xl border border-slate-200 hover:border-[#10B981]/60 hover:bg-[#F0FDF4] transition-colors p-4 md:p-5 group"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${
                        plan.type === "evaluation"
                          ? "bg-[#0F172A] text-white"
                          : "bg-[#D1FAE5] text-[#065F46]"
                      }`}
                    >
                      {plan.type === "evaluation" ? "Evaluation" : "Daily Note"}
                    </span>
                    <span className="font-figtree text-sm font-medium text-[#0F172A]">
                      {formatDate(plan.date)}
                    </span>
                  </div>
                  <p className="font-figtree text-sm text-[#475569] line-clamp-2">
                    {planPreview(plan)}
                  </p>
                </button>
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}
